import React, { useContext, useState, useEffect } from "react";
import Head from "next/head";
import Router from "next/router";
import Swal, { SweetAlertOptions } from "sweetalert2";
import api from "~/services/api";
import { analyticsScheduleEvent } from "~/services/analytics";
import Spinner from "~/shared/Spinner";
import Button from "~/shared/Btn";
import { Header, Content, Footer } from "./styles";
import { Row, Col } from "react-bootstrap";
import { categories, subcategories } from "./options";
import { formatBrazil } from "~/utils/date";
import useAuth from "~/hooks/useAuth";
import { toast } from "react-toastify";
import { MultiSelectFormContext } from "~/contexts/MultiSelectForm";
import { MarketPlaceCreationJob } from "~/contexts/MultiSelectForm/types";
import { BsPencilFill } from "react-icons/bs";
import WarningModal from "./WarningModal";
import MissingFieldsModal from "./MissingFieldsModal";

type SchedullingConfirmationProps = {
    goToStep?: (step: number) => void;
    prevStep?: () => void;
    nextStep?: () => void;
    currentStep?: number;
    utmSource?: string;
};

// TODO: isso deveria ser declardo em arquivos raiz, mas por falta de local apropriado estou declarando aqui
declare global {
    interface Window {
        dataLayer: any;
    }
}

const SchedullingConfirmation = ({ currentStep, goToStep, nextStep, utmSource }: SchedullingConfirmationProps) => {
    const { values, updateStepStorage, clearLocalStorageSteps, openWarningModal, setOpenWarningModal } =
        useContext(MultiSelectFormContext);

    const { user, account } = useAuth();
    const [loading, setLoading] = useState(false);
    const [userPhone, setUserPhone] = useState(user?.celular);

    useEffect(() => {
        setUserPhone(user?.celular);
    }, [user?.celular]);

    const localValues: MarketPlaceCreationJob = JSON.parse(localStorage.getItem("values"));
    updateStepStorage("confirmation", currentStep);

    const segment = categories.find((category) => category.id === localValues?.category);
    const subCatregory = subcategories.find((category) => category.id === localValues?.subcategory);

    if (!account) {
        const params = new URLSearchParams(window.location.search);
        params.set("step", "3");

        Router.push(`/login?to=marketplace/book?${params.toString()}`);
    }

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        params.set("step", "3");

        window.history.pushState({ step: 3 }, "", `/marketplace/book?${params.toString()}`);
    }, []);

    const addressAndDate = localStorage.getItem("addressAndDate");
    const briefingStep = localStorage.getItem("briefing_step");

    const redirectFn = () => {
        clearLocalStorageSteps(["addressAndDate", "confirmation", "briefing_step", "values", "totalSteps"]);

        if (account) {
            return Router.push("/dashboard");
        }

        Router.push("/");
    };

    const handleCancel = () => {
        const params: SweetAlertOptions = {
            title: "Deseja cancelar o agendamento?",
            icon: "warning",
            confirmButtonColor: "#DD6B55",
            showCancelButton: true,
            confirmButtonText: "Confirmar cancelamento",
            cancelButtonText: "Sair",
            showLoaderOnConfirm: true,
            preConfirm: async () => {
                clearLocalStorageSteps([
                    "addressAndDate",
                    "confirmation",
                    "briefing_step",
                    "values",
                    "totalSteps",
                    "redirect",
                ]);

                if (user) {
                    return Router.push("/marketplace/dashboard");
                }

                Router.push("/");
            },
        };

        Swal.fire(params);
    };

    const handleSubmit = async (isTest = false) => {
        setLoading(true);
        const actionAreaId = localValues.category !== 7 ? localValues.category : localValues.subcategory;

        const postFields = {
            isTest,
            ...localValues,
            nome: user.nome,
            email: user.email,
            celular: user?.celular ?? userPhone,
            endereco: localValues.street ? localValues.street : "a_combinar",
            idAreaAtuacao: actionAreaId,
            cep: localValues.zipcode ? localValues.zipcode : "00000-000",
            numero: localValues.number ? localValues.number : "a_combinar",
            bairro: localValues.neighborhood,
            cidade: localValues.city,
            estado: localValues.state,
            job_date: localValues.date ? `${localValues.date}T${localValues.hour}:00.000-0300` : null,
            complemento: localValues.complement,
            data: localValues.date ? localValues.date : null,
            hora: localValues.hour,
            duracao: localValues.duration,
            briefing: localValues.briefing,
            price: 0,
            marketplaceVersion: "v2",
            utm_source: utmSource,
        };

        return api.post("/v2/jobs/marketplace", postFields)
            .then(response => {
                if (response.data.status == "1") {
                    localStorage.setItem("jobId", String(response.data.jobId));
                    if (window.dataLayer) {
                        window.dataLayer.push({ ecommerce: null });
                        window.dataLayer.push({
                            event: "begin_checkout",
                            ecommerce: {
                                items: [{
                                    item_name: `Sessão de ${segment?.name}`,
                                    item_id: segment?.id,
                                    price: 0,
                                    item_category: segment.name,
                                    item_category2: subCatregory?.name,
                                    index: 1,
                                    quantity: 1
                                }]
                            }
                        });
                    }
                    return nextStep();
                }

                return toast("Erro ao enviar proposta por favor tente novamente.");
            })
            .catch(error => {
                console.log(error);
                return toast("Erro ao enviar proposta por favor tente novamente.");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const userIsFotop = () => {
        return user?.email?.includes("@fotop.com.br") || user?.email?.includes("@webventure.com.br") || user?.email?.includes("@fotop.com");
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <>
            {!userPhone ? (
                <MissingFieldsModal
                    isOpen={!userPhone}
                    onSave={({ phone }) => {
                        setUserPhone(phone);
                    }}
                />
            ) : (
                ""
            )}

            <WarningModal
                isOpen={openWarningModal}
                onHide={() => setOpenWarningModal(false)}
                redirectFn={redirectFn}
                owner={account?.organization.owner}
            />
            <Head>
                <title>Revisão do job - Fotop Serviços</title>
            </Head>
            <Header>
                <h1>Confirmação de dados para o agendamento</h1>
            </Header>
            <Content>
                <div className="header-content">
                    <div className="bar"><strong>Seus dados</strong></div>
                </div>
                <Row style={{ marginTop: "1rem" }}>
                    <Col>
                        <p>Nome: </p>
                        <span id="userNameTxt">{user?.nome}</span>
                    </Col>
                    <Col>
                        <p>Email:</p>
                        <span id="userEmailTxt">{user?.email}</span>
                    </Col>
                </Row>
            </Content>
            <Content>
                <div className="header-content">
                    <div className="bar">
                        <strong>Segmento e Briefing</strong>
                        <button onClick={() => goToStep(Number(briefingStep))}>
                            <BsPencilFill style={{ marginRight: "0.3rem" }} />
                            Editar
                        </button>
                    </div>
                </div>
                {values?.category === 17 ? (
                    <>
                        <Row style={{ marginTop: "1rem" }}>
                            <Col>
                                <p>Segmento: </p>
                                <span>{segment.name}</span>
                            </Col>
                            <Col>
                                <p>Tipo de imóvel:</p>
                                <span>
                                    {values?.extra?.property_type?.value || localValues?.extra?.property_type?.value}
                                </span>
                            </Col>
                            <Col>
                                <p>Tamanho em m²:</p>
                                <span>
                                    {values?.extra?.property_size?.value || localValues?.extra?.property_size?.value}m²
                                </span>
                            </Col>
                        </Row>
                        <Row style={{ marginTop: "1rem" }}>
                            <Col lg={4}>
                                <p>Fotos em área externa:</p>
                                <span>{values?.extra?.external_photos?.value ? "Sim" : "Não"}</span>
                            </Col>
                            <Col lg={4}>
                                <p>Quantidade mínima de fotos:</p>
                                <span>
                                    {values?.extra?.qtd_min_photos?.value ||
                                        localValues?.extra?.qtd_min_photos?.value ||
                                        ""}
                                </span>
                            </Col>
                        </Row>
                        <Row style={{ marginTop: "1rem" }}>
                            <Col>
                                <p>Informações adicionais:</p>
                                <span>
                                    {values?.extra?.adicional_info?.value || localValues?.extra?.adicional_info || ""}
                                </span>
                            </Col>
                        </Row>
                    </>
                ) : (
                    <>
                        <Row>
                            <Col>
                                <p style={{ color: "#222" }}>
                                    Segmento: {segment?.name}
                                    {segment.id === 7 && ` - ${subCatregory.name}`}
                                </p>
                            </Col>
                            <textarea value={values?.briefing || localValues?.briefing} rows={7} readOnly></textarea>
                        </Row>
                    </>
                )}
            </Content>
            <Content>
                <div className="header-content">
                    <div className="bar">
                        <strong>Data e Endereço</strong>
                        <button onClick={() => goToStep(Number(addressAndDate))}>
                            <BsPencilFill style={{ marginRight: "0.3rem" }} />
                            Editar
                        </button>
                    </div>
                </div>
                <Row>
                    <Col>
                        <p>Data:</p>
                        <span>
                            {localValues?.date
                                ? formatBrazil(new Date(`${localValues?.date}T00:00:00.000-0300`))
                                : "A combinar"}
                        </span>
                    </Col>
                    <Col>
                        <p>Horário:</p>
                        <span>{localValues.hour ? `${localValues.hour}hrs` : "A combinar"}</span>
                    </Col>
                    <Col>
                        <p>Duração:</p>
                        <span>{localValues.duration ? `${localValues.duration}hrs` : "A combinar"}</span>
                    </Col>
                </Row>
                <div className="header-content" style={{ marginTop: "2rem" }}>
                    <div className="bar"></div>
                </div>
                <Row>
                    <Col>
                        <p>CEP:</p>
                        <span>{values?.zipcode || localValues?.zipcode}</span>
                    </Col>
                    <Col>
                        <p>Endereço:</p>
                        <span>
                            {localValues?.fullAddress ? localValues?.fullAddress : localValues?.street || "A combinar"}
                        </span>
                    </Col>
                    <Col>
                        <p>Número</p>
                        <span>{values?.number || localValues?.number}</span>
                    </Col>
                </Row>
                <Row>
                    <Col sm={6} lg={4}>
                        <p>Complemento</p>
                        <span>{values?.complement || localValues?.complement}</span>
                    </Col>
                </Row>
            </Content>

            <Footer>
                <Button id="clickConfirmationPrev" onClick={handleCancel} block>
                    Cancelar agendamento
                </Button>
                {userIsFotop() ? (
                    <Button id="clickConfirmationNext" onClick={() => handleSubmit(true)} block>
                        {loading ? <Spinner /> : "Criar job de teste"}
                    </Button>
                ) : (
                    ""
                )}
                <Button id="clickConfirmationNext" onClick={() => handleSubmit()} block>
                    {loading ? <Spinner /> : "Confirmar agendamento"}
                </Button>
            </Footer>
        </>
    );
};

export default SchedullingConfirmation;
